import SkillDrawerButtons from 'components/feature/SkillDrawerButtons/SkillDrawerButtons';
import InfoItemGroup from 'components/molecules/InfoItemGroup/InfoItemGroup';
import DetailsCard from 'components/ui/DetailsCard/DetailsCard';
import { emptyDisplayValue } from 'constants/common';
import { FormikContextType, useField, useFormikContext } from 'formik';
import {
  CompanyRfuModelType,
  SoftSkillFragment,
  useSoftSkillRestoreMutation,
  useSoftSkillSoftDeleteMutation,
} from 'generated/graphql';
import { useEffect, useState } from 'react';
import { EditSoftSkillTraits } from 'views/StaticContent/SoftSkills/drawers/children/EditSoftSkillTraits';
import { SoftSkillExamples } from 'views/StaticContent/SoftSkills/drawers/children/SoftSkillExamples';
import { SoftSkillRelationsForm } from 'views/StaticContent/SoftSkills/drawers/children/SoftSkillRelationsForm';
import SoftSkillsContent from 'views/StaticContent/SoftSkills/drawers/children/SoftSkillsContent';
import {
  formatSoftSkillFormErrors,
  getRelationValues,
  softSkillCategoryOptions,
  softSkillRFUModelTypeLabel,
  TEditSoftSkillFormValues,
} from 'views/StaticContent/SoftSkills/drawers/SoftSkillDrawer.helpers';
import {
  getIndexFromTab,
  getTabFromIndex,
  isBroad,
  SoftSkillDrawerTab,
} from 'views/StaticContent/SoftSkills/SoftSkills.helpers';
import {
  FormikSelectFormField,
  FormikTextInputField,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './EditSoftSkillDrawerContents.module.scss';

type TEditSoftSkillDrawerContentsArgs = {
  isEditMode: boolean;
  setIsEditMode: (b: boolean) => void;
  softSkill: SoftSkillFragment | undefined;
  editTab: SoftSkillDrawerTab;
  setEditTab: (tab: SoftSkillDrawerTab) => void;
  loadingUpdate: boolean;
};

export const EditSoftSkillDrawerContents = ({
  isEditMode,
  setIsEditMode,
  softSkill,
  editTab,
  setEditTab,
  loadingUpdate,
}: TEditSoftSkillDrawerContentsArgs) => {
  const { values } = useFormikContext<TEditSoftSkillFormValues>();
  const onSave = (formProps: FormikContextType<TEditSoftSkillFormValues>) => {
    formProps.validateForm(formProps.values);
    if (formProps.isValid && !formProps.isValidating) {
      formProps.submitForm();
    } else {
      handleMsgType({
        type: TNotification.error,
        dismissable: true,
        title: 'Please fix the following errors',
        message: formatSoftSkillFormErrors(formProps.errors),
      });
    }
  };

  const onCancel = (formProps: FormikContextType<TEditSoftSkillFormValues>) => {
    formProps.resetForm();
    setIsEditMode(false);
  };

  const onArchive = () => {
    if (!isArchived) {
      softSkillSoftDeleteMutation({
        variables: { subId: softSkill?.subId ?? '' },
      });
    } else {
      softSkillRestoreMutation({
        variables: { subId: softSkill?.subId ?? '' },
      });
    }
    setIsArchived(!isArchived);
  };

  const formContext = useFormikContext<TEditSoftSkillFormValues>();
  const { handleMsgType } = useNotification();
  const [isArchived, setIsArchived] = useState(false);

  const [softSkillSoftDeleteMutation, { loading: softDeleteLoading }] =
    useSoftSkillSoftDeleteMutation({
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: `Failed to archive soft skill.`,
          message: error?.message,
          dismissable: true,
          time: 1500,
        });
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: `Soft skill archived!`,
          dismissable: true,
          time: 1500,
        });
      },
    });

  const [softSkillRestoreMutation, { loading: restoreLoading }] =
    useSoftSkillRestoreMutation({
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: `Failed to unarchive soft skill.`,
          message: error?.message,
          dismissable: true,
          time: 1500,
        });
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: `Soft skill unarchived!`,
          dismissable: true,
          time: 1500,
        });
      },
    });

  useEffect(() => {
    setIsArchived(Boolean(softSkill?.deletedAt));
  }, [softSkill]);

  const detailsCardItems = [
    [
      {
        label: 'ID',
        value: softSkill?.id?.toString() ?? emptyDisplayValue,
      },
      {
        label: 'Granularity',
        value: values.granularity ?? emptyDisplayValue,
      },
    ],
    [
      {
        label: 'Slug',
        value: softSkill?.subId ?? emptyDisplayValue,
        hasCopyButton: true,
      },
      {
        label: 'Ownership',
        value: values.owner.label ?? emptyDisplayValue,
        valueNote:
          values.owner.label !== 'Global'
            ? `(${softSkillRFUModelTypeLabel[
            softSkill?.company?.rfuModelType ??
            CompanyRfuModelType.BehaviourBased
            ]
            })`
            : '',
      },
    ],
  ];

  const softSkillOverviewItems = [
    {
      label: 'Name',
      value: softSkill?.name ?? emptyDisplayValue,
      hasCopyButton: true,
    },
    {
      label: 'Description',
      value: softSkill?.description ?? emptyDisplayValue,
      hasCopyButton: true,
      maxWidth: '700px',
    },
    {
      label: 'Taxonomy version',
      value: values.taxonomyVersion?.toString() ?? emptyDisplayValue,
    },
    {
      label: 'Category',
      value: values.category?.label ?? emptyDisplayValue,
    },
  ];

  return (
    <div className={styles.editSoftSkillDrawerContentsContainer}>
      <SkillDrawerButtons
        isEditMode={isEditMode}
        onArchive={onArchive}
        onArchiveLoading={softDeleteLoading || restoreLoading}
        isArchived={isArchived}
        onEdit={() => setIsEditMode(true)}
        onSave={() => onSave(formContext)}
        onSaveLoading={loadingUpdate}
        onCancel={() => onCancel(formContext)}
      />
      <DetailsCard items={detailsCardItems} />
      {!isEditMode ? (
        <InfoItemGroup items={softSkillOverviewItems} />
      ) : (
        <div className={styles.editableFieldsContainer}>
          <FormikTextInputField
            id="name"
            label="Name"
            data-role="soft-skill-name-input"
            data-testid="soft-skill-name-input"
            useFormikField={useField}
            disabled={!isEditMode}
          />
          <FormikTextInputField
            id="description"
            label="Description"
            data-role="soft-skill-description-input"
            data-testid="soft-skill-description-input"
            useFormikField={useField}
            placeholder={'Missing description'}
            disabled={!isEditMode}
          />
          <div className={styles.row}>
            <FormikTextInputField
              id="taxonomyVersion"
              label="Taxonomy version"
              data-role="soft-skill-taxonomy-version-input"
              data-testid="soft-skill-taxonomy-version-input"
              useFormikField={useField}
              placeholder={'Taxonomy version'}
              type="number"
              disabled={!isEditMode}
            />
            <FormikSelectFormField
              id="category"
              label="Category"
              options={softSkillCategoryOptions}
              data-role="category-select"
              placeholder="Category"
              maxMenuHeight={240}
              selectProps={{
                'data-testid': 'role-level-select',
              }}
              useFormikField={useField}
              hasClearIndicator={false}
              isDisabled={!isEditMode}
            />
          </div>
        </div>
      )}

      <Tabs
        className={styles.tabs}
        onSelectTab={index => {
          setEditTab(getTabFromIndex(index));
        }}
        selectedTabIndex={getIndexFromTab(editTab)}
      >
        <TabList className={styles.tabList}>
          <Tab
            className={styles.tab}
            counter={
              values.softSkillTraits.filter(trait => trait?.trait?.label).length
            }
          >
            Traits{' '}
          </Tab>
          <Tab
            className={styles.tab}
            counter={
              getRelationValues(values, 'vertical', isBroad(softSkill)).length
            }
          >
            {isBroad(softSkill) ? 'Children' : 'Parents'}
          </Tab>
          <Tab
            className={styles.tab}
            counter={
              getRelationValues(values, 'horizontal', isBroad(softSkill)).length
            }
          >
            Siblings
          </Tab>
          <Tab
            className={styles.tab}
            counter={
              values.examples?.filter(example => example !== '').length ?? 0
            }
          >
            Examples
          </Tab>
          <Tab
            className={styles.tab}
          >
            Reports content
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {editTab === SoftSkillDrawerTab.TRAITS && (
              <EditSoftSkillTraits isEditMode={isEditMode} />
            )}
          </TabPanel>
          <TabPanel>
            {editTab === SoftSkillDrawerTab.RELATIONS && (
              <div className={styles.softSkillDependenciesContainer}>
                <SoftSkillRelationsForm
                  isBroadSoftSkill={isBroad(softSkill)}
                  isEditMode={isEditMode}
                  relationType={'vertical'}
                />
              </div>
            )}
          </TabPanel>
          <TabPanel>
            {editTab === SoftSkillDrawerTab.SIBLINGS && (
              <div className={styles.softSkillRelationsContainer}>
                <SoftSkillRelationsForm
                  isBroadSoftSkill={isBroad(softSkill)}
                  isEditMode={isEditMode}
                  relationType={'horizontal'}
                />
              </div>
            )}
          </TabPanel>
          <TabPanel>
            {editTab === SoftSkillDrawerTab.EXAMPLES && (
              <div className={styles.softSkillExamplesContainer}>
                <SoftSkillExamples isEditMode={isEditMode} />
              </div>
            )}
          </TabPanel>
          <TabPanel>
            {editTab === SoftSkillDrawerTab.CONTENT && (
              <div className={styles.softSkillReportsContentContainer}>
                <SoftSkillsContent
                  softSkillId={softSkill?.id ?? 0}
                />
              </div>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};
